import { Link, useParams } from "@tanstack/react-router";

import ShowNotes from "../components/ShowNotes";
import { useStationPage } from "../components/StationLayout";
import { usePageData } from "../lib/api";
import {
  type EpisodeDetail,
  episodePagePath,
  formatEpisodeDate,
} from "../lib/publicPages";

/**
 * One episode, below the station's shared header (see StationLayout): the
 * page a shared episode link lands on. Notes, art, and the tracklist only —
 * playback is app-only, so no audio URL ever reaches this page's payload.
 */
const EpisodePage = () => {
  const { stationSlug, showSlug, episodeId } = useParams({
    from: "/$stationSlug/shows/$showSlug/episodes/$episodeId",
  });
  const { status } = useStationPage();
  const state = usePageData<EpisodeDetail>(
    episodePagePath(stationSlug, episodeId),
  );

  if (state.kind === "loading") {
    return <p className="station__quiet mono">Loading…</p>;
  }
  if (state.kind !== "ready") {
    return (
      <div className="station__detail">
        <h2 className="station__detail-name">
          {state.kind === "missing"
            ? "No such episode."
            : "Couldn't load this episode."}
        </h2>
        <p className="station__blurb">
          {state.kind === "missing"
            ? "There's no episode at this address — it may have been removed or made private."
            : "Try again in a moment."}
        </p>
        <Link className="btn" to="/$stationSlug" params={{ stationSlug }}>
          Back to {status.name}
        </Link>
      </div>
    );
  }

  const episode = state.data;
  const art = episode.image_url || episode.show.image_url;

  return (
    <div className="station__detail">
      <nav className="station__crumbs mono" aria-label="Breadcrumb">
        <Link to="/$stationSlug" params={{ stationSlug }}>
          {status.name}
        </Link>
        <span aria-hidden> / </span>
        <Link
          to="/$stationSlug/shows/$showSlug"
          params={{ stationSlug, showSlug }}
        >
          {episode.show.name}
        </Link>
        <span aria-hidden> / </span>
        <Link
          to="/$stationSlug/shows/$showSlug/episodes"
          params={{ stationSlug, showSlug }}
        >
          Episodes
        </Link>
      </nav>

      {art && <img className="station__detail-art" src={art} alt="" />}

      <h2 className="station__detail-name">{episode.title}</h2>

      <p className="station__when mono">
        {formatEpisodeDate(episode.start_date)}
      </p>

      <a className="btn station__app" href="/beta">
        Listen in the app
      </a>

      {episode.show_notes && <ShowNotes text={episode.show_notes} />}

      {episode.tracks.length > 0 && (
        <div className="station__recent">
          <span className="eyebrow">Tracklist</span>
          <ol className="station__tracks">
            {episode.tracks.map((track) => (
              <li key={`${track.offset_seconds}·${track.name}·${track.artist}`}>
                <span className="station__track">
                  {track.name}
                  {track.artist && (
                    <span className="station__artist"> — {track.artist}</span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

export default EpisodePage;
